import React, { useState, useRef } from "react";
import { useSelector } from "react-redux";
import emailjs from "@emailjs/browser";
import ReCAPTCHA from "react-google-recaptcha";
import "./contact.css";

const textData = {
  formTitle: ["SKICKA OSS ETT MEDDELANDE", "SEND US A MESSAGE"],
  name: ["Namn", "Name"],
  phone: ["Telefonnummer", "Phone Number"],
  email: ["E-post", "Email"],
  subject: ["Ämne", "Subject"],
  message: ["Meddelande", "Message"],
  submit: ["Skicka", "Send"],
  sending: ["Skickar...", "Sending..."],
  success: ["Tack! Vi hör av oss snart.", "Thank you! We'll get back to you soon."],
  error: ["Något gick fel. Försök igen.", "Something went wrong. Please try again."],
  recaptchaError: ["Verifiera att du inte är en robot.", "Please verify that you are not a robot."],
};

function ContactForm() {
  const userLanguage = useSelector((state) => state.app.language);
  const recaptchaRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    subject: "",
    message: "",
  });
  const [status, setStatus] = useState("");
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  const hasRecaptcha = recaptchaSiteKey && recaptchaSiteKey !== "YOUR_RECAPTCHA_SITE_KEY";

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Verify reCAPTCHA if enabled
    if (hasRecaptcha) {
      const recaptchaValue = recaptchaRef.current?.getValue();
      if (!recaptchaValue) {
        setStatus("recaptcha-error");
        setTimeout(() => setStatus(""), 5000);
        return;
      }
    }
    
    setStatus("sending");

    const serviceID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      phone: formData.phone,
      subject: formData.subject,
      message: formData.message,
      to_email: "admin@vert.farm",
    };

    emailjs
      .send(serviceID, templateID, templateParams, publicKey)
      .then(
        () => {
          setStatus("success");
          setFormData({
            name: "",
            phone: "",
            email: "",
            subject: "",
            message: "",
          });
          if (recaptchaRef.current) {
            recaptchaRef.current.reset();
          }
          setTimeout(() => setStatus(""), 5000);
        },
        (error) => {
          console.error("Email send failed:", error);
          setStatus("error");
          setTimeout(() => setStatus(""), 5000);
        }
      );
  };

  return (
    <div className="ContactFormOuter">
      <div className="ContactFormTitle">
        {textData.formTitle[userLanguage]}
      </div>
      <form className="ContactFormInner" onSubmit={handleSubmit}>
        <div className="ContactFormRow">
          <div className="ContactFormGroup">
            <label className="ContactFormLabel">
              {textData.name[userLanguage]}
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="ContactFormInput"
              required
            />
          </div>
          <div className="ContactFormGroup">
            <label className="ContactFormLabel">
              {textData.phone[userLanguage]}
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="ContactFormInput"
            />
          </div>
        </div>
        <div className="ContactFormRow">
          <div className="ContactFormGroup">
            <label className="ContactFormLabel">
              {textData.email[userLanguage]}
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="ContactFormInput"
              required
            />
          </div>
          <div className="ContactFormGroup">
            <label className="ContactFormLabel">
              {textData.subject[userLanguage]}
            </label>
            <input
              type="text"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              className="ContactFormInput"
              required
            />
          </div>
        </div>
        <div className="ContactFormGroup ContactFormGroupFull">
          <label className="ContactFormLabel">
            {textData.message[userLanguage]}
          </label>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            className="ContactFormTextarea"
            rows="6"
            required
          />
        </div>
        {hasRecaptcha && (
          <div className="ContactFormRecaptcha">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={recaptchaSiteKey}
              theme="light"
            />
          </div>
        )}
        <button
          type="submit"
          className="ContactFormButton"
          disabled={status === "sending"}
        >
          {status === "sending"
            ? textData.sending[userLanguage]
            : textData.submit[userLanguage]}
        </button>
        {status === "success" && (
          <div className="ContactFormMessage ContactFormSuccess">
            {textData.success[userLanguage]}
          </div>
        )}
        {status === "error" && (
          <div className="ContactFormMessage ContactFormError">
            {textData.error[userLanguage]}
          </div>
        )}
        {status === "recaptcha-error" && (
          <div className="ContactFormMessage ContactFormError">
            {textData.recaptchaError[userLanguage]}
          </div>
        )}
      </form>
    </div>
  );
}

export default ContactForm;
